'use client'

import { create } from 'zustand'
import { getVoiceConfig, type VoiceConfig } from '@/api/client'

/** How long a fetched config is trusted before the next ensure() refetches. */
export const TTL_MS = 5 * 60 * 1000
/** A failed fetch is remembered only briefly, so a blip doesn't hide the mic for minutes. */
export const FAIL_TTL_MS = 5 * 1000

interface VoiceConfigState {
  /** Per-team cache. `null` = fetch failed / unavailable (treated as "no voice"). */
  byTeam: Record<string, { config: VoiceConfig | null; fetchedAt: number }>
  /** Load once per team (deduped, TTL-cached). Safe to call from every render-effect. */
  ensure: (teamId: string, opts?: { force?: boolean }) => Promise<void>
  /** Write a config the settings page just saved, so the composer reacts at once. Voids any fetch still in flight. */
  set: (teamId: string, config: VoiceConfig) => void
  /** Forget everything (logout / account switch). */
  clear: () => void
}

const inflight = new Map<string, Promise<void>>()
// Bumped whenever a team's cached value is written or everything is cleared; a
// fetch that started before the bump must not overwrite what came after.
const epochs = new Map<string, number>()
let globalEpoch = 0
const epochOf = (teamId: string) => `${globalEpoch}:${epochs.get(teamId) ?? 0}`

export const useVoiceConfigStore = create<VoiceConfigState>((set, get) => ({
  byTeam: {},
  ensure: (teamId, opts) => {
    if (!teamId) return Promise.resolve()
    const hit = get().byTeam[teamId]
    const ttl = hit?.config ? TTL_MS : FAIL_TTL_MS
    if (!opts?.force && hit && Date.now() - hit.fetchedAt < ttl) return Promise.resolve()
    const pending = inflight.get(teamId)
    if (pending) return pending
    const started = epochOf(teamId)
    const write = (config: VoiceConfig | null) => {
      if (epochOf(teamId) !== started) return // superseded by set()/clear()
      set((s) => ({ byTeam: { ...s.byTeam, [teamId]: { config, fetchedAt: Date.now() } } }))
    }
    const p = getVoiceConfig(teamId)
      .then((config) => write(config))
      .catch(() => {
        // A forced refresh that fails must not wipe a config we already have (it would hide the mic for FAIL_TTL).
        if (!(opts?.force && hit?.config)) write(null)
      })
      .finally(() => {
        if (inflight.get(teamId) === p) inflight.delete(teamId)
      })
    inflight.set(teamId, p)
    return p
  },
  set: (teamId, config) => {
    epochs.set(teamId, (epochs.get(teamId) ?? 0) + 1)
    inflight.delete(teamId)
    set((s) => ({ byTeam: { ...s.byTeam, [teamId]: { config, fetchedAt: Date.now() } } }))
  },
  clear: () => {
    globalEpoch++
    inflight.clear()
    set({ byTeam: {} })
  },
}))
